import { CustomModelAutomaticMutationSideEffectConfigs } from "../types";
import { extractRequestFromQueryKey } from "../../../keys";
import { TellescopeModelFromName } from "../../helper-types";

type Organization = TellescopeModelFromName<"organizations">;

/**
 * Mirrors the server-side Twilio `buy-tfn` purchase on a cached org: the purchased number is set as
 * `twilioNumber` if none exists yet, otherwise appended to `twilioNumbers`. Idempotent — returns the
 * org unchanged if the number is already attached.
 */
const attachTwilioNumberToOrg = (org: Organization, phoneNumber: string): Organization => {
  if (org.twilioNumber === phoneNumber || org.twilioNumbers?.includes(phoneNumber)) return org;
  if (!org.twilioNumber) return { ...org, twilioNumber: phoneNumber };
  return { ...org, twilioNumbers: [...(org.twilioNumbers ?? []), phoneNumber] };
};

/**
 * Mirrors the server-side `SES` `delete-domain` mutation on a cached org: the deleted domain is
 * removed from `verifiedEmailDomains` and every `*@domain` alias is stripped from `fromEmails`.
 */
const removeDomainFromOrg = (org: Organization, domain: string): Organization => ({
  ...org,
  verifiedEmailDomains: (org.verifiedEmailDomains ?? []).filter((d) => d.domain !== domain),
  fromEmails: (org.fromEmails ?? []).filter((email) => !email.endsWith(`@${domain}`)),
});

/**
 * Side effect configurations for the integrations model.
 *
 * Handles:
 * - Stripe subscription mutations (cancel, undo-cancel, pause, resume, update-payment-method) which need to
 *   invalidate the Stripe subscriptions query to refresh the list.
 * - Twilio `buy-tfn` purchases, which attach the purchased number to the org (set as `twilioNumber` if none
 *   exists yet, otherwise appended to `twilioNumbers`) and refresh the Twilio `actions` status.
 * - SES `delete-domain` deletions, which remove the domain from the org's `verifiedEmailDomains` and strip
 *   any `*@domain` aliases from `fromEmails`.
 */
export const INTEGRATIONS_MUTATION_SIDE_EFFECT_CONFIGS: CustomModelAutomaticMutationSideEffectConfigs<"integrations"> =
  {
    proxy_write: (request, _response, context) => {
      // Stripe subscription mutations: refresh the Stripe subscriptions list.
      if (request.integration === "Stripe" && request.type?.startsWith("subscription-")) {
        return {
          proxy_read: {
            exact: false,
            predicate: ({ queryKey }) => {
              const queryRequest = extractRequestFromQueryKey(queryKey);
              return queryRequest?.integration === "Stripe" && queryRequest?.type === "subscriptions";
            },
          },
        };
      }

      // Twilio toll-free number purchase: attach the number to the org and refresh the Twilio actions status.
      if (request.integration === "Twilio" && request.type === "buy-tfn") {
        const phoneNumber = request.query?.phoneNumber;
        if (typeof phoneNumber !== "string") return {};

        const businessId = context?.session?.userInfo.businessId;

        return {
          // No updater → invalidate so availableActions/phoneNumbers refetch after the purchase.
          proxy_read: {
            exact: false,
            predicate: ({ queryKey }) => {
              const queryRequest = extractRequestFromQueryKey(queryKey);
              return queryRequest?.integration === "Twilio" && queryRequest?.type === "actions";
            },
          },
          // The org document can be cached under either getOne or getSome, so mirror the change in both.
          _otherModels: {
            organizations: {
              getOne: {
                exact: false,
                updater: (oldData) => {
                  if (!oldData || !businessId || oldData.id !== businessId) return oldData;
                  return attachTwilioNumberToOrg(oldData, phoneNumber);
                },
              },
              getSome: {
                exact: false,
                updater: (oldData) => {
                  if (!oldData || !businessId) return oldData;
                  return {
                    ...oldData,
                    pages: oldData.pages.map((page) =>
                      page.map((org) => (org.id === businessId ? attachTwilioNumberToOrg(org, phoneNumber) : org))
                    ),
                  };
                },
              },
            },
          },
        };
      }

      // SES domain deletion: remove the domain from the org and strip its aliases from fromEmails.
      // The org document can be cached under either getOne or getSome, so mirror the change in both.
      if (request.integration === "SES" && request.type === "delete-domain") {
        const domain = request.id;
        if (typeof domain !== "string") return {};

        const businessId = context?.session?.userInfo.businessId;

        return {
          _otherModels: {
            organizations: {
              getOne: {
                exact: false,
                updater: (oldData) => {
                  if (!oldData || !businessId || oldData.id !== businessId) return oldData;
                  return removeDomainFromOrg(oldData, domain);
                },
              },
              getSome: {
                exact: false,
                updater: (oldData) => {
                  if (!oldData || !businessId) return oldData;
                  return {
                    ...oldData,
                    pages: oldData.pages.map((page) =>
                      page.map((org) => (org.id === businessId ? removeDomainFromOrg(org, domain) : org))
                    ),
                  };
                },
              },
            },
          },
        };
      }

      return {};
    },
  };
